/**
 * Sets `sizes` on each responsive <picture>'s <source>s from the rendered
 * size of its <img> (see `views/vendor/responsive-images/responsiveImage.blade.php`).
 *
 * Sources render with `sizes="1px"` so only the blurred placeholder loads
 * until this runs; the browser then picks the right srcset candidate for
 * the image's real size and the device pixel ratio.
 *
 * - A ResizeObserver keeps `sizes` in step with layout changes. Hidden
 *   images (0 wide, e.g. the mobile/desktop variant not in use) are left
 *   at 1px so they never download a full image.
 * - A MutationObserver picks up images added after mount (Livewire feeds,
 *   live-preview morphs) and re-applies `sizes` if a morph resets it.
 *
 * create() runs on hard load and after each SPA page swap; cleanup() runs
 * before the swap.
 */
const SELECTOR = 'img[data-statamic-responsive-images]'

export default class {
  constructor () {
    this.create()
  }

  create () {
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(({ target }) => this.update(target))
    })

    this.mutationObserver = new MutationObserver(this.onMutation.bind(this))
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['sizes', 'srcset']
    })

    document.querySelectorAll(SELECTOR).forEach(img => this.resizeObserver.observe(img))
  }

  cleanup () {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.mutationObserver) this.mutationObserver.disconnect()

    this.resizeObserver = null
    this.mutationObserver = null
  }

  onMutation (mutations) {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes') {
        const img = mutation.target.parentNode?.querySelector?.(SELECTOR)
        if (img) this.update(img)
        return
      }

      mutation.addedNodes.forEach(node => {
        this.findImages(node).forEach(img => this.resizeObserver.observe(img))
      })

      mutation.removedNodes.forEach(node => {
        this.findImages(node).forEach(img => this.resizeObserver.unobserve(img))
      })
    })
  }

  findImages (node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return []
    if (node.matches(SELECTOR)) return [node]

    return node.querySelectorAll(SELECTOR)
  }

  update (img) {
    const width = this.requiredWidth(img)
    if (!width) return

    const sizes = `${width}px`

    img.parentNode.querySelectorAll('source').forEach(source => {
      // Only write on change, so our own write doesn't loop back through
      // the MutationObserver.
      if (source.getAttribute('sizes') !== sizes) source.setAttribute('sizes', sizes)
    })
  }

  /**
   * CSS pixel width the image needs to cover its box. For `object-fit:
   * cover` the image is scaled to fill the box's height too, so a tall box
   * needs a wider source than its own width.
   */
  requiredWidth (img) {
    const { width, height } = img.getBoundingClientRect()
    if (!width || !height) return 0

    if (getComputedStyle(img).objectFit === 'cover') {
      const ratio = img.getAttribute('width') / img.getAttribute('height')
      if (ratio) return Math.ceil(Math.max(width, height * ratio))
    }

    return Math.ceil(width)
  }
}
